import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Building2, Package, Warehouse } from 'lucide-react';
import clsx from 'clsx';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { DataTable } from '../../../../components/ui/DataTable';
import { StockTotals } from '../../../../components/warehouses/StockTotals';
import {
  fetchKardexMovements,
  fetchKardexSummary,
} from '../../../../features/stock/stockKardexThunks';
import {
  clearKardex,
  setKardexPage,
  setKardexRowsPerPage,
} from '../../../../features/stock/stockKardexSlice';
import { selectCurrentWarehouse } from '../../../../features/warehouseContext/warehouseContextSlice';
import {
  formatQuantity,
  movementDetailLabel,
  movementTypeLabel,
} from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';

const formatMoment = (value) =>
  value
    ? new Date(value).toLocaleString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

/**
 * `/warehouse/inventory/:productId`: the movements that explain a balance.
 *
 * Scoped to the warehouse in use, like every screen of the module: with one
 * picked it is the kardex of the product there, and with none it runs across
 * every warehouse in reach and each row says which one it happened in.
 */
export const InventoryKardex = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();

  const { product, totals, summaryLoading, movements, totalCount, loading, error, filters } =
    useSelector((state) => state.stockKardex);
  const warehouse = useSelector(selectCurrentWarehouse);

  useEffect(() => {
    dispatch(fetchKardexSummary(productId));
  }, [dispatch, productId, warehouse?.id]);

  useEffect(() => {
    dispatch(fetchKardexMovements(productId));
  }, [dispatch, productId, warehouse?.id, filters.page, filters.rowsPerPage]);

  useEffect(() => () => dispatch(clearKardex()), [dispatch]);

  // A product whose balances are all gone has no consolidated row, but its
  // movements still carry who it is.
  const identity = product || movements[0] || null;
  const sku = identity?.product_sku || `Producto ${productId}`;

  const columns = [
    {
      header: 'FECHA',
      cell: (row) => <span className="text-text-secondary">{formatMoment(row.occurred_at)}</span>,
    },
    {
      header: 'MOVIMIENTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[260px]" title={row.notes || undefined}>
          <span className="font-medium text-ninja-dark dark:text-white">
            {movementTypeLabel(row.movement_type)}
          </span>
          <span className="text-xs text-text-muted mt-0.5 truncate" data-testid="kardex-detail">
            {movementDetailLabel(row)}
          </span>
          {/* The reason, in sight: an adjustment without it is a number nobody can explain. */}
          {row.notes && (
            <span className="text-xs text-text-secondary italic mt-0.5 truncate" data-testid="kardex-notes">
              {row.notes}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'UBICACIÓN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-ninja-dark dark:text-white">
            {row.slot_name || 'Sin ubicar'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">
            {[row.warehouse_name, row.lot && `Lote ${row.lot}`].filter(Boolean).join(' · ')}
          </span>
        </div>
      ),
    },
    {
      header: 'CANTIDAD',
      cell: (row) => {
        const delta = Number(row.qty_delta);
        return (
          <span
            data-testid="kardex-delta"
            className={clsx('font-semibold tabular-nums', delta < 0 ? 'text-danger' : 'text-success')}
          >
            {delta > 0 ? '+' : ''}
            {formatQuantity(row.qty_delta)}
          </span>
        );
      },
    },
    {
      header: 'SALDO',
      cell: (row) => (
        <span className="tabular-nums">
          {row.balance_after === null ? '-' : formatQuantity(row.balance_after)}
        </span>
      ),
    },
    {
      header: 'QUIÉN',
      cell: (row) => <span className="text-text-secondary">{row.actor_email || 'sistema'}</span>,
    },
  ];

  return (
    <div data-testid="inventory-kardex-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Inventario" to={INVENTORY_PATH} current={sku} />

      <DetailHero
        media={<Package size={36} />}
        title={identity?.product_name || sku}
        meta={
          <>
            <span className="font-mono font-semibold text-ninja-dark dark:text-white">{sku}</span>
            {identity?.company_name && (
              <span className="flex items-center gap-1.5">
                <Building2 size={14} /> {identity.company_name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Warehouse size={14} /> {warehouse ? warehouse.name : 'Todas mis bodegas'}
            </span>
          </>
        }
      />

      {(product || summaryLoading) && <StockTotals totals={totals} />}

      <p className="text-sm text-text-secondary">
        Del más reciente al más antiguo. El kardex no se edita: una corrección es un movimiento
        nuevo.
      </p>

      <DataTable
        columns={columns}
        data={movements}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchKardexMovements(productId))}
        itemLabel="movimientos"
        emptyMessage={`Sin movimientos registrados${warehouse ? ` en ${warehouse.name}` : ''}.`}
        onPageChange={(page) => dispatch(setKardexPage(page))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setKardexRowsPerPage(rowsPerPage))}
      />
    </div>
  );
};
